/**
 * @file SideNav.jsx
 * @description Side Navigation Bar
 *
 * @version 1.0.0
 * @author Carmelino Galang
 * @copyright Copyright (c) 2024, NetCyberOps LLC
 */

import React from "react";
import {NavLink, useLocation} from "react-router-dom";

import "./SideNav.css";
import SideNavLogo from "./SideNavLogo";

import Sorting from "@assets/icons/sorting.svg?react";
import Home from "@assets/icons/home.svg?react";

import {useSidebar} from "@context/SidebarContext";

import OverviewIcon from "@assets/icons/side-nav/Active/Overview-Active.svg?react";
import EmailSecurity from "@assets/icons/side-nav/Active/Email-Active.svg?react";
import EndpointSecurity from "@assets/icons/side-nav/Active/Endpoint-Active.svg?react";
import SIEM from "@assets/icons/side-nav/Active/SIEM-Active.svg?react";
import VulnerabilityManagement from "@assets/icons/side-nav/Active/Vulnerability-Management-Active.svg?react";
import SKUIcon from "@assets/icons/side-nav/Active/SKU-Managment-Active.svg?react";
import AdminToolsIcon from "@assets/icons/side-nav/Active/Admin-Tools-Active.svg?react";
import InvestigationsIcon from "@assets/icons/side-nav/Active/Investigations-Active.svg?react";
import SettingsIcon from "@assets/icons/side-nav/Active/Settings-Active.svg?react";
import DarkWebIcon from "@assets/icons/side-nav/Active/Dark-Web-Monitoring-Active.svg?react";
import {useScope} from "@context/ScopeContext.jsx";
import Tip from "@components/Tip/Tip";

const DASHBOARDS = [
    {
        id: "dashboard-overview",
        name: "Customer Overview",
        path: "/dashboards/overview",
        icon: <OverviewIcon className="side-nav-icon dash-icon"/>
    },
    {
        id: "dashboard-endpoint",
        name: "Endpoint Security",
        path: "/dashboards/endpoint",
        icon: <EndpointSecurity className="side-nav-icon dash-icon"/>
    },
    {
        id: "dashboard-vuln",
        name: "Vulnerabilities",
        path: "/dashboards/vulnerabilities",
        icon: <VulnerabilityManagement className="side-nav-icon dash-icon"/>
    },
    {
        id: "dashboard-siem",
        name: "SIEM",
        path: "/dashboards/siem",
        icon: <SIEM className="side-nav-icon dash-icon"/>
    },
    {
        id: "dashboard-email",
        name: "Email Security",
        path: "/dashboards/email",
        icon: <EmailSecurity className="side-nav-icon dash-icon"/>
    },
    {
        id: "dashboard-darkweb",
        name: "Dark Web Monitoring",
        path: "/dashboards/darkweb",
        icon: <DarkWebIcon className="side-nav-icon dash-icon"/>
    },
]

// renders tooltip for sidenav icons
export default function SideNav({
                                    user,
                                    userType,
                                    userFeatures,
                                    userPermissions,
                                }) {
    const {selectedScope} = useScope();
    const {
        isExpanded,
        screenWidth,
        activeLink,
        setActiveLink,
        setIsExpanded,
        displayToolTipIcon,
        setDisplayToolTipIcon,
    } = useSidebar();
    const location = useLocation();

    const toggleMenu = () => {
        setIsExpanded(!isExpanded);
        setDisplayToolTipIcon(isExpanded);
    };

    const setLinkActive = (linkString) => {
        setActiveLink(linkString);
        if (screenWidth < 760) {
            setIsExpanded(!isExpanded);
        }
    };

    // const closeMenu = () => {
    //   if (isExpanded) {
    //     setIsExpanded(false);
    //     setDisplayToolTipIcon(true);
    //   }
    // };

    return (
        <div className="relative side-nav-container">
            {screenWidth < 1280 && isExpanded && (
                <div className="sidebar-overlay" onClick={toggleMenu}></div>
            )}
            <div
                id="sidenav"
                className={`${isExpanded ? "expand" : ""} ${screenWidth < 1280 && screenWidth >= 760 && isExpanded
                    ? "overly-fixed-expand"
                    : screenWidth < 760 && !isExpanded
                        ? "w-0 me-0 ms-0"
                        : screenWidth < 760 && isExpanded
                            ? "w-auto overly-fixed-expand"
                            : ""
                }`}
            >
                <div className="links-container d-flex flex-column overflow-hidden">
                    {screenWidth < 1280 && isExpanded && (
                        <Sorting
                            onClick={toggleMenu}
                            className={`${isExpanded ? "collapse-icon-active" : ""
                            } cursor-pointer collapse-icon-sidenav ms-4 mb-4 `}
                        />
                    )}
                    <div className="w-100" onClick={toggleMenu}>
                        <SideNavLogo isExpanded={isExpanded}/>
                    </div>

                    <div className="topLinks-container w-100">
                        {!userFeatures && (
                            <NavLink
                                onClick={() => setLinkActive("Home")}
                                to="/"
                                className={`nav-link ${location.pathname === "/" ? "nav-link-active" : ""
                                }`}
                            >
                                <Tip show={isExpanded} placement="right" text="Home" tooltipClassName="tooltip-side-nav">
                                    <span className="side-nav-icon-wrapper">
                                        <Home className="side-nav-icon "/>
                                    </span>
                                </Tip>
                                <span className={"side-nav-link-title"}>Home</span>
                            </NavLink>
                        )}
                        {DASHBOARDS.map((dash, index) => (
                            <React.Fragment key={index}>
                                {(userType === "employee" || (userType !== "employee" && dash["id"] !== "dashboard-darkweb")
                                    || (dash["id"] === "dashboard-darkweb" && userFeatures?.has("dashboard-darkweb")
                                        && userPermissions?.has("read-exposure"))) && (
                                    <NavLink
                                        to={dash["path"]}
                                        onClick={() => setLinkActive(dash["name"])}
                                        className={`nav-link ${location.pathname === dash["path"]
                                            ? "nav-link-active"
                                            : ""
                                        } ${dash["id"] !== "dashboard-darkweb" && !userFeatures?.has(dash["id"]) ?
                                            "nav-link-demo-dash" : ""}`}
                                    >
                                        {/*Do not use a conditional Tip for icons loaded from file ID, to prevent unnecessary re-renders.*/}
                                        <Tip
                                            placement={"right"}
                                            text={
                                                <>
                                                    {dash["name"]}{dash["id"] !== "dashboard-darkweb" &&
                                                    !userFeatures?.has(dash["id"]) ? " (Demo)" : ""}
                                                </>
                                            }
                                            tooltipClassName={`tooltip-side-nav ${isExpanded ? 'tooltip-hidden' : ''}`}
                                        >
                                        <span className="side-nav-icon-wrapper">
                                            {dash['icon']}
                                        </span>
                                        </Tip>
                                        <span className={"side-nav-link-title"}>{dash["name"]}</span>
                                    </NavLink>
                                )}
                            </React.Fragment>
                        ))}
                    </div>
                    <div className="divider-container w-100">
                        <hr className="nav-divider"/>
                    </div>
                    <div className="topLinks-container w-100">
                        {userPermissions.has("read-investigations") && (
                            <NavLink
                                to="/investigations"
                                onClick={() => setLinkActive("Investigations")}
                                className={`nav-link ${location.pathname.startsWith("/investigations") ? "nav-link-active" : ""}`}
                            >
                                <Tip show={displayToolTipIcon} placement="right" text="Investigations" tooltipClassName="tooltip-side-nav">
                                    <span className="side-nav-icon-wrapper">
                                        <InvestigationsIcon className="side-nav-icon"/>
                                    </span>
                                </Tip>
                                <span className={"side-nav-link-title"}>Investigations</span>
                            </NavLink>
                        )}
                        {userType !== "customer" && selectedScope?.type !== "customer" && (
                            <NavLink
                                to="/sku"
                                onClick={(e) => {
                                    if (selectedScope?.type === "demo") {
                                        e.preventDefault();
                                        return;
                                    }
                                    setLinkActive("SKU Management")
                                }}
                                className={`nav-link 
                                    ${selectedScope?.type === "demo" ? "nav-link-demo-dash" : ""} 
                                    ${location.pathname.startsWith("/sku") ? "nav-link-active" : ""
                            }`}
                            >
                                <Tip show={displayToolTipIcon} placement="right" text="SKU Management" tooltipClassName="tooltip-side-nav">
                                    <span className="side-nav-icon-wrapper">
                                        <SKUIcon className="side-nav-icon"/>
                                    </span>
                                </Tip>
                                <span className={"side-nav-link-title"}>SKU Management</span>
                            </NavLink>
                        )}

                        <NavLink
                            to="/settings/account"
                            onClick={(e) => {
                                if (selectedScope?.type === "demo") {
                                    e.preventDefault();
                                    return;
                                }
                                setLinkActive("Settings")
                            }}
                            className={`nav-link 
                                    ${selectedScope?.type === "demo" ? "nav-link-demo-dash" : ""} 
                                    ${location.pathname.startsWith("/settings") ? "nav-link-active" : ""
                            }`}
                        >
                            <Tip show={displayToolTipIcon} placement="right" text="Settings" tooltipClassName="tooltip-side-nav">
                                <span className="side-nav-icon-wrapper">
                                    <SettingsIcon className="side-nav-icon"/>
                                </span>
                            </Tip>
                            <span className={"side-nav-link-title"}>Settings</span>
                        </NavLink>

                        {userType === "employee" && selectedScope?.type === "NCO" && (
                            <NavLink
                                to="/admin-tools"
                                onClick={(e) => {
                                    if (selectedScope?.type === "demo") {
                                        e.preventDefault();
                                        return;
                                    }
                                    setLinkActive("Admin Tools")
                                }}
                                className={`nav-link 
                                    ${selectedScope?.type === "demo" ? "nav-link-demo-dash" : ""} 
                                    ${location.pathname.startsWith("/admin-tools") ? "nav-link-active" : ""
                                }`}
                            >
                                <Tip show={displayToolTipIcon} placement="right" text="Admin Tools" tooltipClassName="tooltip-side-nav">
                                    <span className="side-nav-icon-wrapper">
                                        <AdminToolsIcon className="side-nav-icon"/>
                                    </span>
                                </Tip>
                                <span className={"side-nav-link-title"}>Admin Tools</span>
                            </NavLink>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}
